import React from 'react';
import { createUseStyles } from 'react-jss';
import { DynamicGridActionIcons } from './dynamic-grid-action-icons';
import { Popover } from 'react-tiny-popover';
import { Typography, Toggle } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

const useStyles = createUseStyles({
    actionsOuter: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& $actionsInner': {
            opacity: 0,
            pointerEvents: 'none',
        },
        '&:hover $actionsInner': {
            opacity: 1,
            pointerEvents: 'auto',
        },
    },
    actionsOpen: {
        '& $actionsInner': {
            opacity: 1,
            pointerEvents: 'auto',
        },
    },
    actionsInner: {
        transition: 'opacity 120ms ease',
    },
    popoverContainer: {
        zIndex: 2000,
    },

    popoverOuter: {
        backgroundColor: colors.white,
        borderRadius: 4,
        boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.10), 0px 2px 8px 0px rgba(0, 0, 0, 0.16)',
        padding: '6px 0px',
    },
    popoverInner: {
        padding: '6px 16px',
    },
    popoverContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        '&:hover': {
            backgroundColor: 'rgba(222, 230, 239, 0.3)',
            cursor: 'default',
        },
    },
});

interface PopoverContentProps {
    onToggleRequired: () => void;
    areAllColumnsRequired: boolean;
}

function PopoverContent({ onToggleRequired, areAllColumnsRequired }: PopoverContentProps) {
    const classes = useStyles();

    return (
        <div className={classes.popoverOuter}>
            <div className={classes.popoverInner}>
                <Typography variant="caption" color="textDarkSecondary" style={{ textTransform: 'uppercase' }}>
                    Table Header Row
                </Typography>
            </div>
            <div className={`${classes.popoverInner} ${classes.popoverContent}`}>
                <Typography>Required</Typography>
                <Toggle checked={areAllColumnsRequired} onChange={onToggleRequired} />
            </div>
        </div>
    );
}

type PopoverContentLocationArgs = {
    childRect: DOMRect;
    popoverRect: DOMRect;
    position?: unknown;
};

function getContentLocation({ childRect, popoverRect }: PopoverContentLocationArgs) {
    const wouldOverflowBottom = childRect.y + childRect.height + popoverRect.height > window.innerHeight;

    if (wouldOverflowBottom) {
        return {
            top: childRect.top - 4 - popoverRect.height,
            left: childRect.left - (popoverRect.width - childRect.width),
        };
    }

    return {
        top: childRect.bottom + 4,
        left: childRect.left - (popoverRect.width - childRect.width),
    };
}

interface DynamicGridHeaderActionsProps {
    areAllColumnsRequired: boolean;
    className?: string;
    onPopover?: () => void;
    onToggleRequired: () => void;
    setShowHeaderMenuPopover: (show: boolean) => void;
    showPopover: boolean;
}

export function DynamicGridHeaderActions({
    areAllColumnsRequired,
    className,
    onPopover,
    onToggleRequired,
    setShowHeaderMenuPopover,
    showPopover,
}: DynamicGridHeaderActionsProps) {
    const classes = useStyles();

    return (
        <th className={`${classes.actionsOuter} ${showPopover ? classes.actionsOpen : ''} ${className}`}>
            <div className={classes.actionsInner}>
                <Popover
                    isOpen={showPopover}
                    positions={['bottom', 'top']}
                    contentLocation={getContentLocation}
                    containerClassName={classes.popoverContainer}
                    onClickOutside={() => setShowHeaderMenuPopover(false)}
                    content={
                        <PopoverContent
                            areAllColumnsRequired={areAllColumnsRequired}
                            onToggleRequired={onToggleRequired}
                        />
                    }
                >
                    <div>
                        <DynamicGridActionIcons onPopover={onPopover} />
                    </div>
                </Popover>
            </div>
        </th>
    );
}
